import React, { type Dispatch, type SetStateAction } from 'react';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  StyledTable,
  StyledTableCell,
  StyledTableContainer,
  StyledTablePagination,
  StyledTableRow,
} from './styles';

export interface Column<T> {
  id: keyof T | string;
  label: string | React.ReactNode;
  align?: 'center' | 'left' | 'right';
  render?: (value: T[keyof T], row: T) => React.ReactNode;
  width?: string | number;
  minWidth?: string | number;
  maxWidth?: string | number;
}

type PaginationProps =
  | { serverPagination?: false; totalCount?: never }
  // `data` is already the current page; changing page asks the parent to refetch
  | { serverPagination: true; totalCount: number };

type GenericTableProps<T> = BaseTableProps<T> & PaginationProps;

interface BaseTableProps<T> {
  data: T[];
  columns: Column<T>[];
  rowsPerPage?: number;
  page: number;
  size?: 'small' | 'medium';
  setPage: Dispatch<SetStateAction<number>>;
  handleClickRow?: (event: React.MouseEvent<unknown>, row: T) => void;
  getRowSx?: (row: T) => SxProps<Theme>;
  sx?: SxProps<Theme>;
  tableLayout?: 'auto' | 'fixed';
}

export function TableComponent<T extends { id?: string | number }>({
  data,
  columns,
  rowsPerPage = 10,
  page,
  setPage,
  size = 'medium',
  handleClickRow,
  getRowSx,
  sx,
  tableLayout,
  serverPagination,
  totalCount,
}: GenericTableProps<T>) {
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const paginatedData = serverPagination
    ? data
    : data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const count = serverPagination ? totalCount : data.length;

  return (
    <>
      <StyledTableContainer>
        <StyledTable size={size} sx={{ tableLayout, ...sx }}>
          <TableHead>
            <StyledTableRow>
              {columns.map((col) => (
                <StyledTableCell
                  key={col.id as string}
                  align={col.align || 'left'}
                  col={{ col }}
                >
                  {col.label}
                </StyledTableCell>
              ))}
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, idx) => (
              <StyledTableRow
                key={row.id || idx}
                sx={getRowSx ? getRowSx(row) : undefined}
                onClick={(event: React.MouseEvent<HTMLTableRowElement>) =>
                  handleClickRow ? handleClickRow(event, row) : undefined
                }
              >
                {columns.map((col) => {
                  const value = row[col.id as keyof T];
                  return (
                    <StyledTableCell
                      key={col.id as string}
                      align={col.align || 'left'}
                      col={{ col }}
                    >
                      {col.render ? col.render(value, row) : String(value)}
                    </StyledTableCell>
                  );
                })}
              </StyledTableRow>
            ))}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>
      <StyledTablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[rowsPerPage]}
      />
    </>
  );
}

export default TableComponent;
